import { useState, useMemo } from 'react';
import { generateOmiReport } from '../lib/api';

/**
 * Modale per lanciare l'analisi Deep OMI (compravendite dichiarate AdE) su un
 * immobile del gestionale. L'utente sceglie:
 *  - anni da includere (checkbox, ultimi 5 flaggati di default)
 *  - raggio metri (slider 100-1000 step 100, default 500 — il portale AdE
 *    non accetta valori fuori da questo range)
 *
 * Al submit chiama POST /api/immobili/{id}/omi-report e apre il report generato.
 */
export default function OmiReportModal({ row, onClose }) {
  // Anni disponibili: dal 2020 fino all'anno precedente (l'AdE ha lag di 1 anno
  // sul completamento dei dati OMI dichiarati)
  const currentYear = new Date().getFullYear();
  const anniDisponibili = useMemo(() => {
    const start = 2020;
    const end = currentYear - 1; // ultimo anno completo
    const arr = [];
    for (let y = end; y >= start; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  // Default: ultimi 5 anni (o meno se meno disponibili), tutti flaggati
  const [selezionati, setSelezionati] = useState(() =>
    new Set(anniDisponibili.slice(0, 5))
  );
  const [raggio, setRaggio] = useState(500);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const toggleAnno = (y) => {
    const next = new Set(selezionati);
    next.has(y) ? next.delete(y) : next.add(y);
    setSelezionati(next);
  };

  const submit = async () => {
    if (selezionati.size === 0) {
      setError('Seleziona almeno un anno');
      return;
    }
    setLoading(true);
    setError(null);
    setStatus('Generazione in corso… può richiedere 30 sec – 5 min');
    try {
      const res = await generateOmiReport(row.id, {
        raggio,
        anni: Array.from(selezionati).sort((a, b) => a - b),
      });
      if (res.code === 'no_data') {
        // Response 200 con nessun risultato — mostro un messaggio dedicato
        setError(res.error);
        setStatus(null);
        return;
      }
      if (res.code === 'over_limit') {
        // Portale AdE hard-limit (>50 atti). Se abbiamo un raggio suggerito
        // lo applichiamo per facilitare il retry.
        setError(res.error);
        setStatus(null);
        if (res.raggio_suggerito && res.raggio_suggerito < raggio) {
          setRaggio(res.raggio_suggerito);
        }
        return;
      }
      if (res.code === 'portal_timeout') {
        setError(res.error);
        setStatus(null);
        return;
      }
      if (res.url) {
        window.open(res.url, '_blank', 'noopener');
        onClose && onClose();
      } else {
        setError('Report generato ma URL non disponibile');
      }
    } catch (e) {
      const code = e?.response?.data?.code;
      const msg = e?.response?.data?.error || e?.message || 'Errore';
      if (code === 'spid_expired') {
        setError('Sessione SPID AdE scaduta — occorre rifare il login manuale su Chrome VNC prima di procedere.');
      } else if (code === 'chrome_down') {
        setError('Chrome CDP non raggiungibile (sessione AdE non avviata). Contatta l\'amministratore.');
      } else if (e?.code === 'ECONNABORTED' || /timeout/i.test(msg)) {
        setError('Timeout — il portale AdE ci sta mettendo troppo a rispondere. Riprova con raggio più stretto (es. 300m).');
      } else {
        setError(msg);
      }
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="omi-modal__overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="omi-modal" onClick={(e) => e.stopPropagation()}>
        <div className="omi-modal__header">
          <div>
            <h2 className="omi-modal__title">Analisi Deep OMI</h2>
            <p className="omi-modal__sub">
              Compravendite dichiarate (AdE) · {row.comune || '—'} · {row.indirizzo || '—'}
            </p>
          </div>
          <button className="omi-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        </div>

        <div className="omi-modal__body">
          {/* --- Anni --- */}
          <div className="omi-modal__group">
            <label className="omi-modal__label">Anni</label>
            <div className="omi-modal__anni">
              {anniDisponibili.map((y) => (
                <label key={y} className={`omi-modal__anno ${selezionati.has(y) ? 'is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selezionati.has(y)}
                    onChange={() => toggleAnno(y)}
                    disabled={loading}
                  />
                  <span>{y}</span>
                </label>
              ))}
            </div>
            <div className="omi-modal__hint">
              {selezionati.size} anni selezionati · più anni = più atti = tempo di elaborazione più lungo
            </div>
          </div>

          {/* --- Raggio --- */}
          <div className="omi-modal__group">
            <label className="omi-modal__label">
              Raggio di ricerca: <strong>{raggio} m</strong>
            </label>
            <input
              type="range"
              min={100}
              max={1000}
              step={100}
              value={raggio}
              onChange={(e) => setRaggio(Number(e.target.value))}
              disabled={loading}
              className="omi-modal__slider"
            />
            <div className="omi-modal__ticks">
              <span>100m</span>
              <span>500m</span>
              <span>1000m</span>
            </div>
            <div className="omi-modal__hint">
              Il portale AdE accetta solo valori da 100 a 1000 metri. Aumentare il
              raggio può portare a &gt; 50 atti — in quel caso la ricerca AdE
              va in timeout. Raggio consigliato: 300–500 m.
            </div>
          </div>

          {error && <div className="omi-modal__error">{error}</div>}
          {status && !error && <div className="omi-modal__status">{status}</div>}
        </div>

        <div className="omi-modal__footer">
          <button
            className="btn"
            onClick={onClose}
            disabled={loading}
          >
            Annulla
          </button>
          <button
            className="btn btn--solid"
            onClick={submit}
            disabled={loading || selezionati.size === 0}
          >
            {loading ? 'Generazione…' : 'Genera report OMI'}
          </button>
        </div>
      </div>

      <style>{`
        .omi-modal__overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,.5);
          display: flex; align-items: center; justify-content: center;
          z-index: 9999; padding: 20px;
        }
        .omi-modal {
          background: #fff; max-width: 560px; width: 100%;
          max-height: 90vh; overflow-y: auto;
          border: 3px solid #000;
          font-family: 'Courier New', monospace;
        }
        .omi-modal__header {
          padding: 18px 20px;
          border-bottom: 3px solid #000;
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 15px; background: #fafafa;
        }
        .omi-modal__title { margin: 0; font-size: 20px; text-transform: uppercase; letter-spacing: -.3px; }
        .omi-modal__sub  { margin: 6px 0 0; font-size: 12px; color: #666; }
        .omi-modal__close {
          background: transparent; border: 0; font-size: 28px; cursor: pointer;
          line-height: 1; padding: 0 4px; color: #666;
        }
        .omi-modal__close:hover { color: #000; }

        .omi-modal__body { padding: 20px; }
        .omi-modal__group { margin-bottom: 24px; }
        .omi-modal__group:last-child { margin-bottom: 0; }
        .omi-modal__label {
          display: block; font-size: 12px; text-transform: uppercase;
          letter-spacing: 1px; font-weight: 700; margin-bottom: 10px;
        }
        .omi-modal__anni {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          gap: 8px;
        }
        .omi-modal__anno {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 10px; border: 2px solid #000; cursor: pointer;
          font-weight: 700; user-select: none; background: #fff;
        }
        .omi-modal__anno.is-on { background: #000; color: #fff; }
        .omi-modal__anno input { cursor: pointer; accent-color: #c1121f; }

        .omi-modal__slider {
          width: 100%; height: 8px; margin: 8px 0;
          accent-color: #c1121f;
        }
        .omi-modal__ticks {
          display: flex; justify-content: space-between; font-size: 11px; color: #888;
        }

        .omi-modal__hint {
          font-size: 11px; color: #666; margin-top: 8px; line-height: 1.4;
        }

        .omi-modal__error, .omi-modal__status {
          margin-top: 12px; padding: 10px 12px; font-size: 13px;
          border: 2px solid; text-align: center;
        }
        .omi-modal__error  { border-color: #c1121f; background: #fee; color: #c1121f; }
        .omi-modal__status { border-color: #0a7; background: #efe; color: #0a7; }

        .omi-modal__footer {
          padding: 15px 20px; border-top: 3px solid #000; background: #fafafa;
          display: flex; justify-content: flex-end; gap: 10px;
        }
        .omi-modal__footer .btn {
          padding: 10px 18px; border: 2px solid #000; background: #fff;
          font-family: inherit; font-size: 13px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 1px; cursor: pointer;
        }
        .omi-modal__footer .btn--solid { background: #000; color: #fff; }
        .omi-modal__footer .btn:disabled { opacity: .5; cursor: not-allowed; }

        @media (max-width: 600px) {
          .omi-modal { max-height: 100vh; height: 100%; }
          .omi-modal__anni { grid-template-columns: repeat(3, 1fr); }
        }
      `}</style>
    </div>
  );
}
